/**
 * TypeScript interfaces for Claims feature
 * Frontend data types with Date objects for date fields
 */

/**
 * Resolution Status enum
 * Matches backend Prisma enum ResolutionStatus
 */
export enum ResolutionStatus {
  IN_REVIEW = "IN_REVIEW",
  SUBMITTED = "SUBMITTED",
  CLOSED = "CLOSED",
  NO_APPEAL_NEEDED = "NO_APPEAL_NEEDED",
}

/**
 * Resolution history entry
 * Stored in resolutionHistory JSONB array
 */
export interface IResolutionHistoryEntry {
  status: ResolutionStatus;
  notes?: string;
  userId: string;
  userName: string;
  timestamp: string; // ISO 8601
}

/**
 * Resolution status labels for display
 */
export const RESOLUTION_STATUS_LABELS: Record<
  ResolutionStatus | "null",
  string
> = {
  [ResolutionStatus.IN_REVIEW]: "In Review",
  [ResolutionStatus.SUBMITTED]: "Submitted",
  [ResolutionStatus.CLOSED]: "Closed",
  [ResolutionStatus.NO_APPEAL_NEEDED]: "No Appeal Needed",
  null: "Not Reviewed",
};

/**
 * Resolution status colors (PrimeReact Tag severities)
 * Note: PrimeReact Tag only supports 'success' | 'info' | 'warning' | 'danger' | undefined
 */
export const RESOLUTION_STATUS_COLORS: Record<
  ResolutionStatus | "null",
  "danger" | "warning" | "info" | "success" | undefined
> = {
  null: "danger", // Red - Not Reviewed
  [ResolutionStatus.IN_REVIEW]: "warning", // Orange - In Review
  [ResolutionStatus.SUBMITTED]: "info", // Blue - Submitted
  [ResolutionStatus.CLOSED]: "success", // Green - Closed
  [ResolutionStatus.NO_APPEAL_NEEDED]: undefined, // Default gray - No Appeal Needed
};

/**
 * Filter options for resolution status dropdown
 */
export const RESOLUTION_FILTER_OPTIONS = [
  { label: "Not Reviewed", value: "not_reviewed" },
  { label: "In Review", value: "in_review" },
  { label: "All", value: "all" },
] as const;

/**
 * Payment type enum for filtering claims
 * Note: "underpayment" as a business concept remains - this represents claims where payer paid less than expected
 */
export type PaymentType =
  | "all"
  | "underpayment"
  | "overpayment"
  | "correct"
  | "zero_paid";

// Unified filter options - includes both payment types and categories
// Values starting with CATEGORY_ are sent to categories parameter
// Other values are sent to paymentTypes parameter
export const PAYMENT_TYPE_OPTIONS: { label: string; value: string }[] = [
  { label: "All Payment Types", value: "all" },
  { label: "Underpayments", value: "underpayment" },
  { label: "Overpayments", value: "overpayment" },
  { label: "Correct Payments", value: "correct" },
  { label: "Zero-Paid Claims", value: "zero_paid" },
  {
    label: "Charges Below Contract Rate",
    value: "CATEGORY_CHARGE_BELOW_CONTRACT_RATE",
  },
];

export interface IMissingCode {
  code: string;
  quantity: number;
}

export interface ILineItemAdjustment {
  group: string;
  code: string;
  amount: number;
}

export interface ILineItemReference {
  qualifier: string;
  value: string;
}

export interface ILineItemRemark {
  qualifier: string;
  code: string;
}

export interface ILineItem {
  cptCode: string;
  lineProcedureCode: string | null;
  modifier: string | null;
  revenueCode: string | null;
  lineControlNumber: string | null;
  quantity: number;
  billedUnits: number;
  lineCharge: number;
  lineAllowed: number;
  linePaid: number;
  lineTotalAdjAmount: number;
  patientResponsibility?: number; // Sum of all PR adjustments for this line
  adjustments: ILineItemAdjustment[];
  references: ILineItemReference[];
  remarks: ILineItemRemark[];
}

/**
 * Payer type enum matching Prisma schema
 */
export type PayerType =
  | "UhcMedicareAdvantage"
  | "OriginalMedicare"
  | "Commercial";

/**
 * Matched Order (estimate) data from claims-to-estimates matching
 */
export interface IMatchedOrder {
  id: string;
  dateOfService: string; // ISO 8601
  amountInCents: number;
  serviceDetails: any;
  benefitDetails: any;
  patient: {
    firstName: string;
    lastName: string;
  };
  payer?: {
    type: PayerType | null;
  } | null;
  gfeQuote?: {
    remainingDeductibleInCents: number | null;
    outOfPocketMaxInCents: number | null;
    flatCopayInCents: number | null;
    coinsurancePercent: number | null;
    pricingBreakdown: any;
  } | null;
}

export interface IEdi837ServiceLine {
  code: string;
  revenueCode?: string;
  modifier?: string;
  quantity: number;
  chargeAmount?: number;
}

export interface IMatched837Claim {
  claimNumber: string;
  chargeAmount: number;
  typeOfBill: string | null;
  placeOfService: string | null;
  patientClass: "Inpatient" | "Outpatient" | "Unknown";
  payerClaimControlNumber: string | null;
  patientName: string | null;
  serviceDate: string | null;
  providerNPI: string | null;
  drgCode: string | null;
  caseSignature: string | null;
  frequencyCode: string;
  isAuthoritative: boolean;
  serviceLines: IEdi837ServiceLine[];
}

export interface IClaim {
  id: string;
  patientName: string;
  patientDOB: Date | null;
  patientAccountNumber: string | null;
  claimNumber: string | null;
  claimPayerId: string | null;
  claimPayerName: string;
  claimPayerGroupNumber: string | null;
  claimNetworkStatus: string | null;
  clearPayerName: string | null;
  clearParentPayerName: string | null;
  payerId: number | null; // Numeric payer ID for fetching allowables
  serviceDate: Date;
  chargeAmount: number | null; // Display in dollars (divide by 100)
  expectedAmount: number | null; // Display in dollars (divide by 100)
  claimAmount: number; // Display in dollars (divide by 100)
  amountDifference: number | null; // Display in dollars (divide by 100)
  caseSignature: string;
  lineItems?: ILineItem[]; // Line-level details from EDI file
  category: string | null; // Categorizes type of payment discrepancy
  patientClass?: "INPATIENT" | "OUTPATIENT" | "UNKNOWN" | null;
  drgCode?: string | null; // CLP11 from 835 - DRG code for inpatient claims (e.g., "807")
  typeOfBill?: string | null; // Raw 3-digit TOB from 837 (e.g., "131", "111")
  aiAnalysisResult?: IAIAnalysisResult | null; // AI analysis of claim
  differenceAnalysis?: IDifferenceAnalysisResult | null; // AI difference analysis vs similar claims
  promptVersion?: IPromptVersionSummary; // Prompt version used for AI analysis
  resolutionStatus: ResolutionStatus | null; // Current resolution status
  resolutionHistory?: IResolutionHistoryEntry[]; // Audit trail of status changes
  ediConversionFile?: {
    fileName: string;
    displayName: string;
    createdAt: Date;
  };
  precalculatedAllowable?: {
    id: string;
    calculatedData: any;
    allowableAccuracyStatus: boolean;
    missingCodes: IMissingCode[];
  };
  // 837 Claim Submission Data (Feature CLE-1878)
  matched837Claims?: IMatched837Claim[] | null;
  edi837CaseSignature?: string | null;
  edi837ExpectedAmount?: number | null;
  // DRG LOS/Transfer Pricing (CLE-1885, CLE-1904)
  dischargeStatus?: string | null;
  losOutlierCalculation?: {
    pricingRule: string;
    ruleApplied: boolean;
    ruleStatus: string;
    drgBaseRateInCents: number;
    alos: number | null;
    los: number | null;
    losSource: string | null;
    // LOS outlier fields
    threshold?: number | null;
    multiplier?: number;
    outlierDays?: number;
    perDiemInCents?: number;
    outlierAddOnInCents?: number;
    expectedAllowedInCents?: number;
    // Transfer fields
    dischargeStatus?: string;
    transferPaymentInCents?: number;
    // DRG code used for pricing
    drgCode?: string;
  } | null;
  // High-Cost Outlier (CLE-1886, CLE-1903)
  isHighCostOutlier?: boolean | null;
  outlierPaymentInCents?: number | null;
  outlierCalculation?: {
    inpatientRulesetId: string;
    type: string;
    costToChargeRatio: number;
    fixedLossThresholdInCents: number;
    outlierPercent: number;
    estimatedCostInCents: number;
    triggerAmountInCents: number;
  } | null;
  passThroughCalculation?: {
    type: string;
    totalPassThroughAmountInCents: number;
    codes: {
      code: string;
      inpatientRulesetId: string;
      chargeMultiplier: number;
      thresholdInCents: number;
      chargeInCents: number;
      calculatedAmountInCents: number;
    }[];
  } | null;
  edi837PrecalculatedAllowable?: {
    id: string;
    calculatedData: any;
    allowableAccuracyStatus: boolean;
    missingCodes: IMissingCode[];
  } | null;
  // Order matching - linked estimate
  orderId?: string | null;
  orderMatchConfidence?: number | null; // 0.0-1.0
  orderMatchedAt?: string | null; // ISO 8601
  order?: IMatchedOrder | null;
  // Variance analysis - root causes for claim vs estimate discrepancies
  varianceAnalysis?: IVarianceAnalysis | null;
  // Claim-level summary data for inpatient claims (no SVC lines)
  claimSummaryData?: {
    adjustments?: Array<{ group: string; code: string; amount: number }>;
    coveredAmount?: number | null;
    drgWeight?: string | null;
    drgAmount?: number | null;
    coveredDays?: number | null;
    lengthOfStay?: number | null;
  } | null;
  // NCCI Bypass - per-claim toggle for NCCI edit bypass
  ncciBypassEnabled?: boolean;
}

/**
 * AI Analysis Result interfaces
 */
export interface IAIAnalysisResult {
  analyzedAt: string; // ISO 8601 timestamp
  model: string; // OpenAI model used
  summary: string; // High-level summary of the claim analysis
  effectiveTotals: IAIEffectiveTotals;
  excludedImplants: IAIExcludedImplant[];
  lineItemDiscrepancies: IAILineItemDiscrepancy[];
  classifications: IAIClassification[];
  resolution: IAIResolution;
  variance: IVarianceCalculation;
}

export interface IAIEffectiveTotals {
  originalExpectedAllowed: number; // cents
  originalActualAllowed: number; // cents
  excludedImplantExpected: number; // cents
  excludedImplantActual: number; // cents
  effectiveExpectedAllowed: number; // cents
  effectiveActualAllowed: number; // cents
  underpaymentAmount: number; // cents - business concept: how much payer underpaid
}

export interface IAIExcludedImplant {
  cpt: string;
  modifier: string | null;
  revenueCode: string;
  expectedAllowed: number; // cents
  actualAllowed: number; // cents
  reason: string;
}

export interface IAILineItemDiscrepancy {
  cpt: string;
  modifier: string | null;
  expectedAllowed: number; // cents
  actualAllowed: number; // cents
  adjustmentCodes: string[];
  explanation: string;
}

export interface IAIClassification {
  code: string; // e.g., "C5", "D2"
  category: string; // e.g., "Contract / Calculation Errors"
  title: string; // e.g., "MPPR Error"
  confidence: "High" | "Medium" | "Low";
  explanation: string;
}

export interface IAIResolution {
  hasRealDiscrepancy: boolean;
  appealLetterDraft: string;
}

export interface IVarianceCalculation {
  expectedAllowed: number; // cents
  actualAllowed: number; // cents
  variance: number; // cents
  percentDifference: number; // percentage
}

export interface IClaimFilters {
  accountId: string; // Required
  search?: string;
  startDate?: Date;
  endDate?: Date;
  sortField?: string;
  sortOrder?: "asc" | "desc";
  page?: number;
  pageSize?: number;
  minDifferencePercent?: number; // Minimum difference percentage filter
  chargeBelowContractRateFilter?: "include" | "exclude" | "only";
  categories?: string[]; // Array of category values to filter by (e.g., 'CHARGE_BELOW_CONTRACT_RATE')
  underpaymentOnly?: boolean; // When true, only show records where amountDifference < 0 (true underpayments) - DEPRECATED: use paymentTypes instead
  paymentTypes?: string[]; // Array of payment types to filter by (underpayment, overpayment, correct, zero_paid, all)
  resolutionFilter?: "not_reviewed" | "in_review" | "all"; // Filter by resolution status
  classificationFilter?: string; // AI classification code filter (e.g., "C5", "D2") or "not_analyzed" or "all"
  clearPayerFilter?: string; // Filter by matched Clear payer name
  patientClassFilter?: "inpatient" | "outpatient" | "all"; // Filter by patient class
}

export interface IClaimResponse {
  data: IClaim[];
  totalCount: number;
  page: number;
  pageSize: number;
  message?: string;
}

// Category constants (match backend enum values)
export const CLAIM_CATEGORIES = {
  CHARGE_BELOW_CONTRACT_RATE: "CHARGE_BELOW_CONTRACT_RATE",
} as const;

export const CATEGORY_LABELS: Record<string, string> = {
  [CLAIM_CATEGORIES.CHARGE_BELOW_CONTRACT_RATE]: "Below Contract",
};

export const CATEGORY_COLORS: Record<
  string,
  "info" | "success" | "warning" | "danger"
> = {
  [CLAIM_CATEGORIES.CHARGE_BELOW_CONTRACT_RATE]: "info",
};

/**
 * Underpayment Analysis Prompt interfaces
 * Versioned AI prompts for claim analysis
 * Note: Name kept as "UnderpaymentAnalysisPrompt" to match unchanged backend table name
 */

export interface IUnderpaymentAnalysisPrompt {
  id: string; // UUID
  version: number; // Sequential version number
  createdAt: string; // ISO 8601 timestamp
  identity: string; // First section: Role/identity description
  issueSummary: string; // Second section: STEP 1-2, implant exclusion, discrepancy detection
  classification: string; // Third section: STEP 3, taxonomy, deterministic rules
  appealLetter: string; // Fourth section: Appeal letter template (JSON schema injected automatically)
  finalRules: string; // Fifth section: Final rules and closing instructions
}

export interface IPromptVersionSummary {
  id: string; // UUID
  version: number; // Sequential version number
  createdAt: string; // ISO 8601 timestamp
}

/**
 * Variance Analysis interfaces
 * Root cause analysis for discrepancies between 835 claim data and matched estimates
 */

export interface IVarianceAnalysis {
  /** True if any root cause discrepancy was detected */
  hasDiscrepancy: boolean;
  /** Detailed root cause analysis */
  rootCauses: IRootCauses;
}

export interface IRootCauses {
  /** Allowable amount mismatch - claim vs expected (underpayment/overpayment) */
  allowableMismatch: IAllowableMismatchAnalysis | null;
  /** Case signature change between claim and estimate */
  codeChange: ICodeChangeAnalysis | null;
  /** Implant CPT codes present in one but not the other */
  implantPtResponsibility: IImplantPtResponsibilityAnalysis | null;
  /** Benefit component mismatches (deductible, coinsurance %, OOP, copay) */
  benefitsMismatch: IBenefitsMismatchAnalysis | null;
  /** Payer type mismatch between claim (MA) and estimate (Commercial), or vice versa */
  payerTypeMismatch: IPayerTypeMismatchAnalysis | null;
}

export interface IAllowableMismatchAnalysis {
  /** Whether a mismatch was detected */
  detected: boolean;
  /** Type of mismatch: claim_high (claim > expected, overpayment), claim_low (claim < expected, underpayment), or null */
  type: "claim_high" | "claim_low" | null;
  /** Expected allowed amount in cents (from precalculated allowables) */
  expectedAllowed: number;
  /** Claim allowed amount in cents (actual payer payment from 835) */
  claimAllowed: number;
  /** Variance in cents (claim - expected), positive = claim higher than expected */
  variance: number;
}

export interface IQuantityChange {
  /** CPT code */
  code: string;
  /** Quantity in claim */
  claimQty: number;
  /** Quantity in estimate */
  estimateQty: number;
}

export interface ICodeChangeAnalysis {
  /** Whether a code change was detected */
  detected: boolean;
  /** Claim case signature (CPT:qty format) */
  claimSignature: string;
  /** Estimate case signature (CPT:qty format) */
  estimateSignature: string;
  /** CPT codes in claim but not in estimate */
  codesAdded: string[];
  /** CPT codes in estimate but not in claim */
  codesDropped: string[];
  /** CPT codes in both but with different quantities */
  quantityChanges: IQuantityChange[];
}

export interface IImplantPtResponsibilityAnalysis {
  /** Whether an implant discrepancy was detected */
  detected: boolean;
  /** Implant CPT codes in claim but not in estimate */
  implantsInClaimOnly: string[];
  /** Implant CPT codes in estimate but not in claim */
  implantsInEstimateOnly: string[];
}

export interface IBenefitsMismatchAnalysis {
  /** Whether any benefit mismatch was detected */
  detected: boolean;
  /** Deductible mismatch (claim > estimate only) */
  deductible: IBenefitMismatchDetail | null;
  /** Coinsurance percentage mismatch (any direction) */
  coinsurancePercent: ICoinsurancePercentMismatch | null;
  /** OOP max mismatch (claim > estimate only) */
  oopMax: IBenefitMismatchDetail | null;
  /** Copay mismatch (claim > estimate only) */
  copay: IBenefitMismatchDetail | null;
}

export interface IBenefitMismatchDetail {
  /** Whether this specific benefit mismatch was detected */
  detected: boolean;
  /** Claim amount in cents */
  claimAmount: number;
  /** Estimate amount in cents */
  estimateAmount: number;
  /** Variance in cents */
  variance: number;
}

export interface ICoinsurancePercentMismatch {
  /** Whether a coinsurance percentage mismatch was detected */
  detected: boolean;
  /** Claim coinsurance percentage */
  claimPercent: number;
  /** Estimate coinsurance percentage */
  estimatePercent: number;
  /** Variance in percentage points */
  variance: number;
}

export interface IPayerTypeMismatchAnalysis {
  /** Whether a payer type mismatch was detected */
  detected: boolean;
  /** Claim payer type */
  claimPayerType: PayerType | null;
  /** Estimate payer type */
  estimatePayerType: PayerType | null;
  /** Type of mismatch: claim is MA and estimate is Commercial, or vice versa */
  mismatchType:
    | "claim_ma_estimate_commercial"
    | "claim_commercial_estimate_ma"
    | null;
}

/**
 * AI Difference Analysis Result
 * Compares an underpaid claim against similar correctly-paid claims
 */
export interface IDifferenceAnalysisResult {
  /** When analysis was performed (ISO 8601 timestamp) */
  analyzedAt: string;
  /** Plain text analysis explaining why this claim was underpaid */
  analysis: string;
  /** IDs of the similar claims used in the comparison */
  similarClaimIds: string[];
  /** OpenAI model used */
  model: string;
}

/**
 * Similar Claims Search interfaces
 * Used for finding correctly-paid claims as evidence for appeals
 */

/** Summary of a similar correctly-paid claim */
export interface ISimilarClaimSummary {
  /** Unique identifier (UUID) */
  id: string;
  /** Claim number from 835 file */
  claimNumber: string | null;
  /** Date of service (ISO 8601 format) */
  serviceDate: string;
  /** Patient full name */
  patientName: string;
  /** Case signature (CPT:qty format, e.g., "19318:2,64445:1") */
  caseSignature: string;
  /** Actual allowed amount from 835 in cents */
  claimAmount: number;
  /** Expected allowed amount per contract in cents */
  expectedAmount: number | null;
  /** Match percentage (0-100) based on overlapping problem CPT codes */
  matchPercentage: number;
  /** Match tier: 'perfect' for 100%, 'partial' for 50-99% */
  matchTier: "perfect" | "partial";
}

/** Response from similar claims search endpoint */
export interface ISimilarClaimsResponse {
  /** Array of similar correctly-paid claims */
  similarClaims: ISimilarClaimSummary[];
  /** Criteria used to find similar claims */
  matchCriteria: {
    /** CPT codes with underpayment from AI analysis (where actualAllowed < expectedAllowed) */
    problemCptCodes: string[];
    /** Payer name used for matching */
    clearPayerName: string;
  };
  /** Total number of similar claims found */
  totalFound: number;
}
